<!-- #region HTML -->
<template>
  <div class="app">
    <alert :SystemErrorAlert="isSystemError" @update:SystemErrorAlert="isSystemError = $event"></alert>
    <div class="main-container form-container">
      <!-- #region Title Section -->
      <div class="steps">
        <div class="title-container">
          <div class="title">
            <label>ログイン</label>
          </div>
        </div>
      </div>
      <div class="login-note-container">
        <p class="login-note">メールアドレス/パスワード変更、アカウント削除のためにはログインが必要です。</p>
      </div>
      <!-- #endregion -->

      <!-- #region Form Section -->
      <form novalidate @submit.prevent="validateUser">
        <md-card class="form-container">
          <!-- #region Form Fields -->
          <div class="md-form">
            <md-field :class="getValidationClass('email')">
              <md-input id="email" v-model="form.email" name="email" type="email" :disabled="sending"
                placeholder="メールアドレスID" @input="clearErrorFlag()" />
              <span v-if="!$v.form.email.required" class="md-error error">入力してください。</span>
              <span v-else-if="!$v.form.email.allemail" class="md-error error">入力されたメールアドレスに誤りはございませんか？</span>
              <span v-else-if="isUserNotConfirmed === true"
                class="md-error error">ユーザー登録が完了しておりません。ユーザー登録を完了してください。</span>
            </md-field>
            <md-field :class="getValidationClass('password')">
              <md-input id="password" v-model="form.password" name="password" :type="passwordInputType"
                :disabled="sending" placeholder="パスワード" @input="clearErrorFlag()" />
              <span class="toggle-password" @click="togglePassword()">
                <img :src="passwordInputType === 'text' ? eyeOpenImg : eyeCloseImg" alt="パスワードを表示" width="100%" />
              </span>

              <span v-if="!$v.form.password.required" class="md-error error">入力してください。</span>
              <span v-else-if="!$v.form.password.minLength" class="md-error error">8文字以上、64文字以下で入力してください。</span>
              <span v-else-if="!$v.form.password.maxLength" class="md-error error">8文字以上、64文字以下で入力してください。</span>
              <span v-else-if="isInvalidPassword === true" class="md-error error">メールアドレスIDまたはパスワードが誤っています。</span>
            </md-field>
          </div>
          <!-- #endregion -->

          <!-- #region Progress Bar -->
          <md-progress-bar v-if="sending" md-mode="indeterminate" />
          <!-- #endregion -->

          <!-- #region Submit Button -->
          <div class="submit-container">
            <md-card-actions>
              <md-button type="submit" class="primary-button" :disabled="sending">ログイン</md-button>
            </md-card-actions>
          </div>
          <!-- #endregion -->

        </md-card>
      </form>
      <!-- #endregion -->

    </div>
  </div>
</template>
<!-- #endregion -->
<!-- #region JavaScript -->
<script>
// #region Imports 
import Alert from '@/layout/Alerts.vue';
import { togglePassword } from '@/utils/base.js';
import { Auth } from 'aws-amplify';
import { validationMixin } from 'vuelidate';
import { maxLength, minLength, required } from 'vuelidate/lib/validators';
import { issueShopAuthCookies } from '@/services/CookieService'
// #endregion 

// #region Constants 
const allemail = (email) => {
  var re = /^((([a-zA-Z0-9])+([a-zA-Z0-9\._-])*)|([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return re.test(email);
}
// #endregion 

export default {
  name: 'Signin',
  mixins: [validationMixin],
  components: {
    "alert": Alert
  },
  data: () => ({
    form: {
      email: null,
      password: null,
    },
    userSaved: false,
    sending: false,
    isInvalidPassword: false,
    isUserNotConfirmed: false,
    isSystemError: false,
    message: '',
    passwordInputType: 'password', // State for password visibility
    eyeOpenImg: require('@/assets/img/eye-open.svg'),
    eyeCloseImg: require('@/assets/img/eye-close.svg'),
  }),
  validations: {
    form: {
      email: {
        required,
        allemail,
      },
      password: {
        required,
        minLength: minLength(8),
        maxLength: maxLength(64),
      },
    },
  },
  methods: {
    togglePassword() {
      this.passwordInputType = togglePassword(this.passwordInputType);
    },
    getValidationClass(fieldName) {
      const field = this.$v.form[fieldName]
      if (this.isInvalidPassword) {
        return {
          'md-invalid': true
        }
      }
      if (this.isUserNotConfirmed) {
        return {
          'md-invalid': true
        }
      }
      if (field) {
        return {
          'md-invalid': field.$invalid && field.$dirty,
        }
      }
    },
    clearForm() {
      this.$v.$reset()
    },
    clearErrorFlag() {
      this.isInvalidPassword = false
      this.isUserNotConfirmed = false
      this.isSystemError = false
    },
    async saveUser() {
      this.clearErrorFlag()
      this.sending = true
      try {
        const user = await Auth.signIn(this.form.email, this.form.password)

        // --- Issue cross-domain cookies for SHOP auto-login 
        try {
          await issueShopAuthCookies({
            idToken: user.signInUserSession.idToken.jwtToken,
          })
        } catch (e) {
          console.error('[Signin] issueShopAuthCookies failed', e)
          // continue flow even if cookie fails; optional: display warning
        }

        this.$store.commit('setUser', user)
        this.$emit('callSignin', user)
      } catch (error) {
        if (error.code === 'NotAuthorizedException' || error.code === 'UserNotFoundException') {
          this.isInvalidPassword = true
        } else if (error.code === 'UserNotConfirmedException') {
          this.isUserNotConfirmed = true
        } else {
          this.isSystemError = true
        }
        this.sending = false
        return
      }
      this.userSaved = true
      this.sending = false
      this.clearForm()
      this.$router.push(this.$route.query.force === 'true' ? this.$store.state.loginTransitionPagePath : '/')
      this.$store.commit('setLoginTransitionPagePath', null)
    },
    async validateUser() {
      this.$v.$touch()
      if (this.$v.$invalid) {
        this.isInvalidPassword = true
        return
      }
      this.isInvalidPassword = false
      this.isSystemError = false
      this.saveUser()
    },
  },
}
</script>
<!-- #endregion -->
<!-- #region Styles -->
<style scoped>
.title-container {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.login-note-container {
  margin-bottom: 30px;
}

.login-note {
  font-family: var(--default-family);
  color: var(--text-primary);
  font-size: var(--text-size-S);
  font-weight: var(--weight-normal);
  line-height: normal;
  margin-top: 8px;
  margin-bottom: 0;
}
</style>
<!-- #endregion -->